'use client';

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import type { PromptLibraryListResponse, PromptLibraryMember, SavedPromptEntry } from '#types/types';

interface PromptLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPrompt?: (entry: SavedPromptEntry) => void;
}

const PROMPT_LIBRARY_LIMIT = 50;

function handleAvatarImageError(event: React.SyntheticEvent<HTMLImageElement>) {
  const target = event.currentTarget;
  const fallback = target.nextElementSibling as HTMLElement | null;
  target.style.display = 'none';
  if (fallback) fallback.style.display = 'flex';
}

function sortMembers(members: PromptLibraryMember[], currentUserId: string | null) {
  return members
    .slice()
    .sort((a, b) => {
      if (currentUserId) {
        if (a.id === currentUserId) return -1;
        if (b.id === currentUserId) return 1;
      }
      if (a.isFaterOrg && !b.isFaterOrg) return 1;
      if (!a.isFaterOrg && b.isFaterOrg) return -1;
      const creditsDiff = (b.creditsSpent ?? 0) - (a.creditsSpent ?? 0);
      if (creditsDiff !== 0) return creditsDiff;
      return a.name.localeCompare(b.name);
    });
}

interface MemberButtonProps {
  member: PromptLibraryMember;
  isActive: boolean;
  onSelect: (memberId: string) => void;
}

const MemberButton = React.memo(function MemberButton({ member, isActive, onSelect }: MemberButtonProps) {
  const handleClick = useCallback(() => {
    onSelect(member.id);
  }, [onSelect, member.id]);

  const initials = member.name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <button
      type="button"
      title={member.name}
      onClick={handleClick}
      className={`relative h-16 w-16 rounded-full border transition-colors ${isActive ? 'border-[#B3D9FF] ring-2 ring-[#B3D9FF]/40' : 'border-neutral-700 hover:border-neutral-500'}`}
    >
      {member.isFaterOrg ? (
        member.imageUrl ? (
          <img
            src={member.imageUrl}
            alt={member.name}
            className="h-full w-full rounded-full object-cover"
            onError={handleAvatarImageError}
          />
        ) : null
      ) : member.id ? (
        <img
          src={`/api/user/avatar?userId=${member.id}`}
          alt={member.name}
          className="h-full w-full rounded-full object-cover"
          onError={handleAvatarImageError}
        />
      ) : null}
      <span className="hidden h-full w-full items-center justify-center rounded-full bg-neutral-800 text-sm font-semibold text-neutral-200">
        {initials || '?'}
      </span>
      <span className="absolute bottom-0 right-0 min-w-[18px] rounded-full border border-neutral-900 bg-neutral-800 px-1 text-[10px] font-semibold leading-4 text-neutral-100">
        {member.promptCount ?? 0}
      </span>
    </button>
  );
});

interface PromptRowProps {
  entry: SavedPromptEntry;
  onSelect: (entry: SavedPromptEntry) => void;
  onContextMenu: (event: React.MouseEvent, entry: SavedPromptEntry) => void;
}

const PromptRow = React.memo(function PromptRow({ entry, onSelect, onContextMenu }: PromptRowProps) {
  const handleClick = useCallback(() => {
    onSelect(entry);
  }, [onSelect, entry]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    onContextMenu(event, entry);
  }, [onContextMenu, entry]);

  return (
    <button
      type="button"
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className="w-full text-left rounded-md border border-neutral-700 px-3 py-2 transition-colors hover:border-neutral-500 hover:bg-neutral-700/40"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="text-sm text-white leading-snug">{entry.prompt ? entry.prompt : '(Empty prompt)'}</div>
        <div className="whitespace-nowrap text-xs text-neutral-400">Copied {entry.count ?? 0}x</div>
      </div>
    </button>
  );
});

export default function PromptLibraryModal({ isOpen, onClose, onSelectPrompt }: PromptLibraryModalProps) {
  const { kernel } = useKernel();
  const [prompts, setPrompts] = useState<SavedPromptEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<PromptLibraryMember[]>([]);
  const [activeMemberId, setActiveMemberId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const isLoadingMoreRef = React.useRef(false);
  const loaderRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const normalizedQuery = useMemo(() => searchQuery.trim(), [searchQuery]);

  const fetchPrompts = useCallback(async (nextPage: number, mode: 'replace' | 'append') => {
    if (!isOpen) return;
    setIsLoading(true);
    setError(null);
    try {
      const data: PromptLibraryListResponse = (await kernel.current?.listPromptLibrary({
        limit: PROMPT_LIBRARY_LIMIT,
        offset: Math.max(0, (nextPage - 1) * PROMPT_LIBRARY_LIMIT),
        ownerId: activeMemberId,
        search: normalizedQuery,
      })) ?? {};
      const nextPrompts = Array.isArray(data.prompts) ? data.prompts : [];
      setPrompts((prev) => {
        if (mode === 'append') {
          const seen = new Set(prev.map((item) => item.id));
          const merged = [...prev];
          nextPrompts.forEach((item: SavedPromptEntry) => {
            if (!seen.has(item.id)) {
              seen.add(item.id);
              merged.push(item);
            }
          });
          return merged;
        }
        return nextPrompts;
      });
      setHasMore(Boolean(data.pagination?.hasMore));
      setPage(nextPage);
      if (Array.isArray(data.members)) {
        setMembers(data.members);
      }
      const nextUserId = data.currentUserId;
      if (nextUserId) {
        setCurrentUserId(nextUserId);
        setActiveMemberId((prev) => prev ?? nextUserId);
      }
    } catch (err) {
      console.error('[PromptLibrary] Failed to load prompts', err);
      setError(err instanceof Error ? err.message : 'Failed to load prompt library.');
    } finally {
      setIsLoading(false);
      isLoadingMoreRef.current = false;
    }
  }, [kernel, isOpen, normalizedQuery, activeMemberId]);

  // fetchPrompts change d'identite des que isOpen, normalizedQuery ou
  // activeMemberId change : un seul effet couvre l'ouverture, la recherche
  // et le changement de membre.
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setPrompts([]);
    setHasMore(false);
    setHasScrolled(false);
    void fetchPrompts(1, 'replace');
  }, [isOpen, fetchPrompts]);

  const handleLoadMore = useCallback(() => {
    if (isLoading || isLoadingMoreRef.current) return;
    if (!hasMore) return;
    isLoadingMoreRef.current = true;
    void fetchPrompts(page + 1, 'append');
  }, [isLoading, hasMore, page, fetchPrompts]);

  useEffect(() => {
    if (!isOpen) return;
    const target = loaderRef.current;
    const root = listRef.current;
    if (!target) return;
    if (!root) return;
    if (!hasScrolled) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !isLoading) {
          handleLoadMore();
        }
      },
      { root, rootMargin: '120px', threshold: 0.1 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [isOpen, handleLoadMore, isLoading, hasScrolled]);

  const handlePromptClick = useCallback(async (entry: SavedPromptEntry) => {
    onSelectPrompt?.(entry);
    if (!entry.id) return;
    try {
      await kernel.current?.incrementPromptLibraryUsage(entry.id);
      setPrompts((prev) =>
        prev.map((item) =>
          item.id === entry.id ? { ...item, count: (item.count ?? 0) + 1 } : item
        )
      );
    } catch (err) {
      console.error('[PromptLibrary] Failed to increment prompt count', err);
    }
  }, [kernel, onSelectPrompt]);

  const handlePromptContextMenu = useCallback((event: React.MouseEvent, entry: SavedPromptEntry) => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentUserId) return;
    if (activeMemberId && activeMemberId !== currentUserId) return;
    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions: [
        {
          label: 'Remove from library',
          onClick: async () => {
            try {
              await kernel.current?.removePromptFromLibrary(entry.id);
              setPrompts((prev) => prev.filter((item) => item.id !== entry.id));
            } catch (err) {
              console.error('[PromptLibrary] Failed to remove prompt', err);
            } finally {
              kernel.current?.setMenuData(null);
            }
          },
        },
      ],
    });
  }, [kernel, currentUserId, activeMemberId]);

  const stopClickPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleSearchQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleListScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if ((event.currentTarget.scrollTop ?? 0) > 0) {
      setHasScrolled(true);
    }
  }, []);

  const sortedMembers = useMemo(() => sortMembers(members, currentUserId), [members, currentUserId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-neutral-900 border border-neutral-700 rounded-2xl w-[82vw] h-[82vh] max-w-6xl flex flex-col overflow-hidden"
        onClick={stopClickPropagation}
      >
        <div className="flex items-center gap-4 border-b border-neutral-700 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">Prompt Library</h2>
          <div className="flex-1 max-w-lg">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchQueryChange}
              placeholder="Search prompts..."
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-[#B3D9FF] focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-neutral-400 hover:text-white"
            aria-label="Close prompt library"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <aside className="w-44 border-r border-neutral-700 p-4 flex-shrink-0 overflow-y-auto">
            <div className="grid grid-cols-2 gap-5 justify-items-center">
              {sortedMembers.map((member) => (
                <MemberButton
                  key={member.id}
                  member={member}
                  isActive={member.id === (activeMemberId ?? currentUserId)}
                  onSelect={setActiveMemberId}
                />
              ))}
            </div>
          </aside>
          <div className="flex-1 p-6 min-h-0 flex flex-col">
            {isLoading && prompts.length === 0 && (
              <p className="text-sm text-neutral-300">Loading saved prompts...</p>
            )}
            {error && !isLoading && (
              <p className="text-sm text-red-400">{error}</p>
            )}
            {!isLoading && !error && prompts.length === 0 && (
              <p className="text-sm text-neutral-300">{normalizedQuery ? 'No prompts match this search.' : 'No prompts yet.'}</p>
            )}
            {!error && prompts.length > 0 && (
              <div
                ref={listRef}
                className="flex flex-col flex-1 min-h-0 overflow-y-auto pr-1"
                onScroll={handleListScroll}
              >
                <div className="space-y-3">
                  {prompts.map((entry) => (
                    <PromptRow
                      key={entry.id}
                      entry={entry}
                      onSelect={handlePromptClick}
                      onContextMenu={handlePromptContextMenu}
                    />
                  ))}
                  {hasMore && !isLoading && prompts.length > 0 && (
                    <div ref={loaderRef} className="flex items-center justify-center py-4 text-neutral-400 text-sm">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-4 w-4 rounded-full border-2 border-neutral-500 border-t-transparent animate-spin" />
                        Loading more...
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

